import {
  Avatar,
  CardActions,
  CardContent,
  CardHeader,
  Dialog,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  IconButton,
  ImageListItem,
  ImageListItemBar,
  Paper,
  Radio,
  TextField,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import { blue } from "@mui/material/colors";
import { useTheme, useMediaQuery } from "@mui/material";
import useToast from "../../../../hooks/useToast";
import { userServices } from "../../../../service/UserService";
import { TYPE_NOTIFICATION } from "../../shares/NotificationBull";
import AppButton from "../../shares/AppButton";
import {
  enleverBaliseHtml,
  getBackendProfilePictureLink,
  showFullName,
} from "../../../../Helpers/utilsHelper";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { CloseOutlined, DeleteOutline, EditOutlined } from "@material-ui/icons";
import { uploadService } from "../../../../service/UploadService";
import StandardSkeletoon from "../../shares/loader/StandardSkeletoon";

const INIT_USER_INFO = {
  nom: "",
  prenoms: "",
  sexe: "",
  adresse: "",
  telephone1: "",
  email: "",
  annee_naissance: "",
  fonction: "",
  photo: "",
  linkedin: "",
  github: "",
  description: "",
};

const INIT_FORM_ERROR = {
  nom_error: null,
  sexe_error: null,
  telephone_error: null,
  mail_error: null,
  fonction_orror: null,
  description_error: null,
  annee_naissance_error: null,
  linkedin_error: null,
  github_error: null,
  photo_error: null,
};

const IdentiteForm = ({ open, onFormClose, idMembre }) => {
  const [currentUser, setCurrentUser] = useState(INIT_USER_INFO);
  const [formErrors, setFormErrors] = useState(INIT_FORM_ERROR);
  const [isSubmited, setIsSubmited] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formLoading, setFormLoading] = useState(true);
  const [userPhoto, setUserPhoto] = useState(null);
  const { Toast, showToast } = useToast();

  const handleFormClose = () => {
    onFormClose();
    setCurrentUser(INIT_USER_INFO);
    setIsSubmited(false);
  };

  const handleChangeMembreIdentite = (e) => {
    const { name, value } = e.target;
    setCurrentUser({ ...currentUser, [name]: value });
  };

  const handleFetchCurrentUser = () => {
    setFormLoading(true);
    userServices
      .getMyProfile()
      .then((res) => {
        if (res && res.success) {
          setCurrentUser(res.data);
          setUserPhoto({ ...userPhoto, photo_link: res.data.photo_link });
        } else {
          setCurrentUser(INIT_USER_INFO);
        }
      })
      .finally(() => {
        setFormLoading(false);
      });
  };

  const handleCheckDataValidation = () => {
    setFormErrors({
      adresse_error: !currentUser.adresse ? "Adresse requis" : null,
      nom_error: !currentUser.nom ? "Nom requis" : null,
      annee_naissance_error: !currentUser.annee_naissance
        ? "Année de naissance requise"
        : null,
      sexe_error: !currentUser.sexe ? "Sexe requis" : null,
      mail_error: null,
      fonction_orror: !currentUser.fonction ? "Fonction requise" : null,
    });
    return Object.values(formErrors).every((item) => item === null);
  };

  const isAllFieldOk = () => {
    return Object.values(formErrors).every((item) => item === null);
  };

  const handleUpdate = () => {
    setSubmitting(true);
    userServices
      .updateUser(currentUser)
      .then((res) => {
        if (res && res.success) {
          showToast(TYPE_NOTIFICATION.SUCCESS, res.message);
          handleFormClose();
        } else {
          showToast(TYPE_NOTIFICATION.ERROR, res.message);
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  useEffect(() => {
    if (userPhoto && userPhoto?.name) {
      handleFileUpload(userPhoto);
    }
  }, [userPhoto]);

  const handleFileUpload = (file) => {
    setFormErrors({
      ...formErrors,
      photo_error: "Telechargement du fichier en cours...",
    });
    uploadService
      .uploadFile(file)
      .then((res) => {
        if (!(res && res.success)) {
          showToast(TYPE_NOTIFICATION.ERROR, enleverBaliseHtml(res.error));
        }
        setCurrentUser({
          ...currentUser,
          photo: res && res.success ? res.data.file_name : null,
          photo_link: res && res.success ? res.data.link : null,
        });
        // setFormErrors({
        //   ...formErrors,
        //   photo_error: res && res.success ? null : enleverBaliseHtml(res.error),
        // });
      })
      .finally(() => {
        setFormErrors({
          ...formErrors,
          photo_error: null,
        });
      });
  };

  const handleSubmit = () => {
    setIsSubmited(true);
    if (isAllFieldOk()) {
      handleUpdate();
    } else {
      showToast(TYPE_NOTIFICATION.ERROR, "Veuillez verifier les champs");
    }
  };

  useEffect(() => {
    if (open) handleFetchCurrentUser();
  }, [open]);

  useEffect(() => {
    if (currentUser) {
      handleCheckDataValidation();
    }
  }, [currentUser]);

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md")); //à partir de taille md

  const fileInputRef = useRef(null);

  const handleEditPhoto = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUserPhoto(file);
      setCurrentUser({ ...currentUser, photo: file.name });
    }
  };

  return (
    <>
      <Toast />
      <Dialog open={open} onClose={handleFormClose} maxWidth="md">
        <Paper sx={{ m: 3 }} elevation={0}>
          <Grid>
            <CardHeader
              title={"Modifier information"}
              sx={{ mt: -2, mb: -2 }}
            />
            <Grid item width={"100%"} display={formLoading ? "" : "none"}>
              <StandardSkeletoon
                show={formLoading}
                row={4}
                col={1}
                escalier={true}
                m={2}
              />
            </Grid>
            <Grid
              maxHeight={"75vh"}
              display={formLoading ? "none" : ""}
              overflow="auto"
            >
              {currentUser && !formLoading && (
                <CardContent>
                  <CardHeader
                    avatar={
                      <Grid display={"flex"} flexDirection={"column"}>
                        <Grid display={"flex"} flexDirection={"row"}>
                          <Avatar
                            variant="rounded"
                            sx={{
                              m: 1,
                              width: "clamp(100px, 15vw, 150px)",
                              height: "clamp(100px, 15vw, 150px)",
                              fontSize: "clamp(40px, 8vw, 80px)",
                              borderColor: formErrors.photo_error
                                ? "error.main"
                                : "text.primary",
                            }}
                            alt={showFullName(
                              currentUser.nom,
                              currentUser.prenoms
                            )}
                            src={
                              userPhoto?.photo_link || currentUser.photo_link
                            }
                          />
                          <Grid
                            display={"flex"}
                            height={"100%"}
                            ml={-11}
                            mt={1}
                            flexDirection={"row"}
                            alignItems={"start"}
                          >
                            <IconButton
                              sx={{
                                "&:hover": {
                                  color: "red",
                                },
                              }}
                              onClick={handleEditPhoto}
                            >
                              <EditOutlined />
                            </IconButton>
                            <input
                              type="file"
                              ref={fileInputRef}
                              accept="image/png, image/jpeg, image/jpg"
                              style={{ display: "none" }}
                              onChange={handleFileChange}
                            />
                            <IconButton
                              sx={{
                                "&:hover": {
                                  color: "red",
                                },
                              }}
                            >
                              <CloseOutlined />
                            </IconButton>
                          </Grid>
                        </Grid>
                        <FormHelperText
                          sx={{
                            display: formErrors.photo_error ? "flex" : "none",
                            color: "error.main",
                          }}
                          id="photo_helper"
                        >
                          {formErrors.photo_error}
                        </FormHelperText>
                      </Grid>
                    }
                  />
                  <Grid
                    container
                    overflow={"auto"}
                    direction={isMdUp ? "row" : "column"}
                  >
                    <Grid item mb={1} xs={12} md={6}>
                      <Grid item m={1.5}>
                        <FormControl fullWidth>
                          <label>Nom *</label>
                          <TextField
                            id="nom"
                            name="nom"
                            onChange={handleChangeMembreIdentite}
                            size="small"
                            type="text"
                            placeholder="Nom"
                            value={currentUser.nom || ""}
                            error={formErrors.nom_error && isSubmited}
                            helperText={isSubmited ? formErrors.nom_error : ""}
                            fullWidth
                            aria-describedby="nom_helper"
                          />
                          <FormHelperText id="nom_helper"></FormHelperText>
                        </FormControl>
                        <FormControl fullWidth>
                          <label>Prenoms</label>
                          <TextField
                            id="prenoms"
                            name="prenoms"
                            onChange={handleChangeMembreIdentite}
                            size="small"
                            type="text"
                            value={currentUser.prenoms || ""}
                            error={formErrors.prenoms_error && isSubmited}
                            helperText={
                              isSubmited ? formErrors.prenoms_error : ""
                            }
                            placeholder="Prenoms"
                            fullWidth
                            aria-describedby="prenoms_helper"
                          />
                          <FormHelperText id="prenoms_helper"></FormHelperText>
                        </FormControl>
                        <FormControl fullWidth>
                          <label>Année de naissance *</label>
                          <TextField
                            id="annee_naissance"
                            name="annee_naissance"
                            onChange={handleChangeMembreIdentite}
                            value={currentUser.annee_naissance || ""}
                            size="small"
                            type="number"
                            error={
                              formErrors.annee_naissance_error && isSubmited
                            }
                            helperText={
                              isSubmited ? formErrors.annee_naissance_error : ""
                            }
                            placeholder="Année de naissance"
                            fullWidth
                            aria-describedby="annee_naissance_helper"
                          />
                          <FormHelperText id="annee_naissance_helper"></FormHelperText>
                        </FormControl>
                        <FormControl fullWidth>
                          <label>Sexe</label>
                          <FormGroup
                            row
                            defaultValue={"M"}
                            onChange={handleChangeMembreIdentite}
                            sx={{ ml: 1 }}
                          >
                            <FormControlLabel
                              // required
                              value={"M"}
                              name="sexe"
                              control={<Radio />}
                              label="Masculin"
                              checked={currentUser.sexe === "M"}
                            />
                            <FormControlLabel
                              // required
                              value={"F"}
                              name="sexe"
                              control={<Radio />}
                              label="Feminin"
                              checked={currentUser.sexe === "F"}
                            />
                          </FormGroup>
                          <FormHelperText
                            id="sexe_helper"
                            sx={{
                              display: formErrors.sexe_error ? "" : "none",
                              color: "error.main",
                            }}
                          >
                            {formErrors.sexe_error}
                          </FormHelperText>
                        </FormControl>
                        <FormControl fullWidth>
                          <label>Adresse</label>
                          <TextField
                            id="adresse"
                            name="adresse"
                            onChange={handleChangeMembreIdentite}
                            size="small"
                            type="text"
                            value={currentUser.adresse || ""}
                            error={formErrors.adresse_error && isSubmited}
                            helperText={
                              isSubmited ? formErrors.adresse_error : ""
                            }
                            placeholder="Adresse"
                            fullWidth
                            aria-describedby="adresse_helper"
                          />
                          <FormHelperText id="adresse_helper"></FormHelperText>
                        </FormControl>
                        <FormControl fullWidth>
                          <label>Telephone *</label>
                          <TextField
                            id="telephone"
                            name="telephone"
                            onChange={handleChangeMembreIdentite}
                            size="small"
                            type="text"
                            value={currentUser.telephone1 || ""}
                            error={formErrors.telephone_error && isSubmited}
                            helperText={
                              isSubmited ? formErrors.telephone_error : ""
                            }
                            placeholder="Téléphone du membre"
                            fullWidth
                            aria-describedby="telephone_helper"
                          />
                          <FormHelperText id="telephone_helper"></FormHelperText>
                        </FormControl>
                        <FormControl fullWidth>
                          <label>Email</label>
                          <TextField
                            id="email"
                            name="email"
                            onChange={handleChangeMembreIdentite}
                            size="small"
                            type="email"
                            value={currentUser.email || ""}
                            error={formErrors.mail_error && isSubmited}
                            helperText={isSubmited ? formErrors.mail_error : ""}
                            placeholder="Email du membre"
                            fullWidth
                            aria-describedby="email_helper"
                          />
                          <FormHelperText id="email_helper"></FormHelperText>
                        </FormControl>
                      </Grid>
                    </Grid>
                    {/* <Divider
                  orientation={isMdUp ? "vertical" : "horizontal"}
                  flexItem
                  sx={{ mx: isMdUp ? 2 : 0, my: isMdUp ? 0 : 2 }}
                /> */}
                    <Grid item xs={12} mb={1} md={6}>
                      <Grid m={1.5}>
                        <FormControl fullWidth>
                          <label>Fonction *</label>
                          <TextField
                            id="fonction"
                            name="fonction"
                            onChange={handleChangeMembreIdentite}
                            size="small"
                            type="text"
                            value={currentUser.fonction || ""}
                            error={formErrors.fonction_orror && isSubmited}
                            helperText={
                              isSubmited ? formErrors.fonction_orror : ""
                            }
                            placeholder="Fonction du membre"
                            fullWidth
                            aria-describedby="fonction_helper"
                          />
                          <FormHelperText id="fonction_helper"></FormHelperText>
                        </FormControl>
                        <FormControl fullWidth>
                          <label>Description</label>
                          <TextField
                            multiline
                            rows={4}
                            id="description"
                            name="description"
                            onChange={handleChangeMembreIdentite}
                            size="small"
                            type="text"
                            value={currentUser.description || ""}
                            error={formErrors.description_error && isSubmited}
                            helperText={
                              isSubmited ? formErrors.description_error : ""
                            }
                            placeholder="Vos descriptions en quelques phrases"
                            fullWidth
                            aria-describedby="description_helper"
                          />
                          <FormHelperText id="description_helper"></FormHelperText>
                        </FormControl>
                        <FormControl fullWidth>
                          <label>LinkedIn</label>
                          <TextField
                            id="linkedin"
                            name="linkedin"
                            onChange={handleChangeMembreIdentite}
                            size="small"
                            type="url"
                            value={currentUser.linkedin || ""}
                            error={formErrors.linkedin_error && isSubmited}
                            helperText={
                              isSubmited ? formErrors.linkedin_error : ""
                            }
                            placeholder="Lien profile LinkedIn"
                            fullWidth
                            aria-describedby="linkedin_helper"
                          />
                          <FormHelperText id="linkedin_helper"></FormHelperText>
                        </FormControl>
                        <FormControl fullWidth>
                          <label>Github</label>
                          <TextField
                            id="github"
                            name="github"
                            onChange={handleChangeMembreIdentite}
                            size="small"
                            type="url"
                            value={currentUser.github || ""}
                            error={formErrors.github_error && isSubmited}
                            helperText={
                              isSubmited ? formErrors.github_error : ""
                            }
                            placeholder="Lien profile Github"
                            fullWidth
                            aria-describedby="github_helper"
                          />
                          <FormHelperText id="github_helper"></FormHelperText>
                        </FormControl>
                        {/* <FormControl fullWidth>
                        <FormGroup sx={{ mt: 3, ml: 1 }}>
                          <FormControlLabel
                            name="autorized"
                            control={<Switch />}
                            label={
                              (currentUser.membre_params.autorized === "1"
                                ? "Autorisé"
                                : " Non autorisé") + " à gerer l'agence / association"
                            }
                            onChange={(e) =>
                              handleSetMembreToAuthorized(
                                e.target.checked ? "1" : "0"
                              )
                            }
                            checked={
                              currentUser.membre_params.autorized === "1"
                            }
                          />
                        </FormGroup>
                      </FormControl> */}
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              )}
            </Grid>
            <CardActions sx={{ justifyContent: "flex-end" }}>
              <AppButton
                loading={submitting}
                disabled={formLoading}
                text={"Enregistrer"}
                size={"medium"}
                variant={"contained"}
                onClick={(e) => handleSubmit()}
              />
              <AppButton
                disabled={formLoading}
                size={"medium"}
                text={"Fermer"}
                variant={"outlined"}
                onClick={(e) => handleFormClose()}
              />
            </CardActions>
          </Grid>
        </Paper>
      </Dialog>
    </>
  );
};

export default IdentiteForm;
